import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2, FileText } from 'lucide-react';
import { migrateFacebookLeadsToFormSubmissions, migrateEligibilityLeadsToFormSubmissions, revertMigration } from '@/scripts/migrateFacebookLeadsToFormSubmissions';
import { supabase } from '@/integrations/supabase/client';

export default function FacebookLeadsMigration() {
  const [isRunning, setIsRunning] = useState(false);
  const [isRunningEligibility, setIsRunningEligibility] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [isSyncingPdfs, setIsSyncingPdfs] = useState(false);
  const [result, setResult] = useState(null);
  const [leadsCount, setLeadsCount] = useState(null);
  const [eligibilityLeadsCount, setEligibilityLeadsCount] = useState(null);

  React.useEffect(() => {
    checkLeadsWithoutSubmission();
    checkEligibilityLeadsWithoutSubmission();
  }, []);

  const checkLeadsWithoutSubmission = async () => {
    try {
      const { count } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('source_platform', 'facebook')
        .is('form_submission_id', null);
      
      setLeadsCount(count);
    } catch (error) {
      console.error('Erro ao contar leads:', error);
    }
  };

  const checkEligibilityLeadsWithoutSubmission = async () => {
    try {
      const { count } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('source_platform', 'eligibility_form')
        .is('form_submission_id', null);
      
      setEligibilityLeadsCount(count);
    } catch (error) {
      console.error('Erro ao contar leads de eligibility:', error);
    }
  };

  const handleSyncPdfs = async () => {
    setIsSyncingPdfs(true);
    setResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('sync-eligibility-pdfs');
      
      if (error) throw error;
      
      setResult({
        success: true,
        logs: JSON.stringify(data, null, 2)
      });
    } catch (error) {
      console.error('Erro ao sincronizar PDFs:', error);
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsSyncingPdfs(false);
    }
  };

  const handleMigrate = async () => {
    setIsRunning(true);
    setResult(null);
    
    try {
      // Redirect console.log to capture the output
      const logs = [];
      const originalLog = console.log;
      console.log = (...args) => {
        logs.push(args.join(' '));
        originalLog(...args);
      };

      await migrateFacebookLeadsToFormSubmissions();
      
      // Restore console.log
      console.log = originalLog;
      
      setResult({
        success: true,
        logs: logs.join('\n')
      });
      
      // Recheck count
      await checkLeadsWithoutSubmission();
      await checkEligibilityLeadsWithoutSubmission();
    } catch (error) {
      console.error('Erro na migração:', error);
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleMigrateEligibility = async () => {
    setIsRunningEligibility(true);
    setResult(null);
    
    try {
      const logs = [];
      const originalLog = console.log;
      console.log = (...args) => {
        logs.push(args.join(' '));
        originalLog(...args);
      };

      await migrateEligibilityLeadsToFormSubmissions();
      
      console.log = originalLog;
      
      setResult({
        success: true,
        logs: logs.join('\n')
      });
      
      await checkLeadsWithoutSubmission();
      await checkEligibilityLeadsWithoutSubmission();
    } catch (error) {
      console.error('Erro na migração:', error);
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsRunningEligibility(false);
    }
  };

  const handleRevert = async () => {
    if (!confirm('Tem certeza que deseja reverter a migração? Isso irá remover todos os form_submissions criados pela migração.')) {
      return;
    }

    setIsReverting(true);
    setResult(null);
    
    try {
      const logs = [];
      const originalLog = console.log;
      console.log = (...args) => {
        logs.push(args.join(' '));
        originalLog(...args);
      };

      await revertMigration();
      
      console.log = originalLog;
      
      setResult({
        success: true,
        logs: logs.join('\n')
      });
      
      await checkLeadsWithoutSubmission();
      await checkEligibilityLeadsWithoutSubmission();
    } catch (error) {
      console.error('Erro ao reverter:', error);
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Migração de Leads do Facebook</h1>
        <p className="text-muted-foreground mt-1">
          Crie form_submissions retroativamente para leads antigos do Facebook
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status da Migração</CardTitle>
          <CardDescription>
            Esta ferramenta migra leads do Facebook que não possuem form_submission_id associado
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {leadsCount !== null && (
            <div>
              <h3 className="font-semibold mb-2">Leads do Facebook</h3>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{leadsCount} leads</strong> do Facebook sem form_submission_id foram encontrados
                </AlertDescription>
              </Alert>
              <div className="flex gap-4 mt-4">
                <Button
                  onClick={handleMigrate}
                  disabled={isRunning || isReverting || leadsCount === 0}
                  className="flex items-center gap-2"
                >
                  {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isRunning ? 'Migrando...' : 'Migrar Leads do Facebook'}
                </Button>
              </div>
            </div>
          )}

          {eligibilityLeadsCount !== null && (
            <div>
              <h3 className="font-semibold mb-2">Leads de Formulário de Elegibilidade</h3>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{eligibilityLeadsCount} leads</strong> de eligibility_form sem form_submission_id foram encontrados
                </AlertDescription>
              </Alert>
              <div className="flex gap-4 mt-4">
                <Button
                  onClick={handleMigrateEligibility}
                  disabled={isRunningEligibility || isReverting || isSyncingPdfs || eligibilityLeadsCount === 0}
                  className="flex items-center gap-2"
                >
                  {isRunningEligibility && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isRunningEligibility ? 'Migrando...' : 'Migrar Leads de Elegibilidade'}
                </Button>
                <Button
                  onClick={handleSyncPdfs}
                  disabled={isRunningEligibility || isReverting || isSyncingPdfs}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {isSyncingPdfs && <Loader2 className="h-4 w-4 animate-spin" />}
                  <FileText className="h-4 w-4" />
                  {isSyncingPdfs ? 'Sincronizando...' : 'Sincronizar PDFs'}
                </Button>
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <Button
              onClick={handleRevert}
              variant="destructive"
              disabled={isRunning || isRunningEligibility || isReverting || isSyncingPdfs}
              className="flex items-center gap-2"
            >
              {isReverting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isReverting ? 'Revertendo...' : 'Reverter Toda Migração'}
            </Button>
          </div>

          {result && (
            <Alert variant={result.success ? "default" : "destructive"}>
              {result.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {result.success ? (
                  <div className="space-y-2">
                    <p className="font-semibold">Migração concluída com sucesso!</p>
                    <pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded mt-2 max-h-96 overflow-auto">
                      {result.logs}
                    </pre>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold">Erro na migração:</p>
                    <p className="text-sm mt-1">{result.error}</p>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="text-sm text-muted-foreground space-y-2 border-t pt-4 mt-4">
            <p className="font-semibold">Como funciona:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Busca todos os leads do Facebook sem form_submission_id</li>
              <li>Cria um registro de form_submission para cada lead usando dados do raw_data</li>
              <li>Atualiza o lead com o form_submission_id criado</li>
              <li>Preserva a data original de criação do lead</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
